import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { useUpdateUserPassword } from '../../api/mutations/useUpdateUserPassword';
import { ChangePasswordForm, changePasswordSchema } from '../../api/schemas/user';
import { Breadcrumb } from '../components/breadcrumb';
import { PageContainer } from '../components/page-container';
import { PageHeader } from '../components/page-header';
import { PageTitle } from '../components/page-title';
import { Button } from '../components/shadcn/button';
import { Input } from '../components/shadcn/input';
import { Label } from '../components/shadcn/label';
import { SubTitle } from '../components/subtitle';

export function UserSettings() {
  const [newPasswordTouched, setNewPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    reset,
    formState: { errors, isSubmitted },
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onChange',
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const { mutate: updateUserPassword, isLoading } = useUpdateUserPassword();

  const { newPassword, confirmPassword } = watch();

  const onSubmit = (data: ChangePasswordForm) => {
    updateUserPassword(
      { newPassword: data.newPassword },
      {
        onSuccess: () => {
          reset();
          setNewPasswordTouched(false);
          setConfirmPasswordTouched(false);
        },
      },
    );
  };

  return (
    <>
      <Breadcrumb items={[{ href: '/', label: 'Home', active: true }]} />

      <PageContainer>
        <PageHeader>
          <PageTitle text="User Settings" />
        </PageHeader>

        <SubTitle text="Change Password" iconClass="lucide--key-square" />

        <form onSubmit={handleSubmit(onSubmit)} className="my-8 max-w-md">
          <div className="mb-6 flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="new-password" className="text-sm font-medium text-gray-700">
                New Password
              </Label>

              {(newPasswordTouched || isSubmitted) && errors.newPassword && (
                <span className="text-xs text-red-600">{errors.newPassword.message}</span>
              )}
            </div>

            <Input
              id="new-password"
              type="password"
              value={newPassword}
              className="w-full"
              autoComplete="off"
              autoCorrect="off"
              {...register('newPassword', {
                onBlur: () => setNewPasswordTouched(true),
                onChange: () => {
                  if (confirmPasswordTouched) {
                    trigger('confirmPassword');
                  }
                },
              })}
            />
          </div>

          <div className="mb-4 flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="confirm-password" className="text-sm font-medium text-gray-700">
                Confirm Password
              </Label>

              {(confirmPasswordTouched || isSubmitted) && errors.confirmPassword && (
                <span className="text-xs text-red-600">{errors.confirmPassword.message}</span>
              )}
            </div>

            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              className="w-full"
              autoComplete="off"
              autoCorrect="off"
              {...register('confirmPassword', {
                onBlur: () => setConfirmPasswordTouched(true),
              })}
            />
          </div>

          <Button variant="primary" disabled={isLoading} type="submit" className="my-4">
            Change Password
          </Button>
        </form>
      </PageContainer>
    </>
  );
}
